import { OverlaySpinner } from '@ankr.com/ui';
import { ReactNode, useEffect } from 'react';

import { Placeholder } from 'modules/common/components/Placeholder';
import { selectBlockchainsLoadingStatus } from 'modules/chains/store/selectors';
import { selectIsSignedOutManually } from 'modules/auth/store/selectors';
import { useAppSelector } from 'store/useAppSelector';
import { useAuthInstance } from 'modules/auth/hooks/useAuthInstance';
import { useCurrentPath } from 'modules/common/hooks/useCurrentPath';
import { useSignInWithRedirectURL } from 'modules/auth/hooks/useSignInWithRedirectURL';

import { RedirectToChainsList } from './components/RedirectToChainsList';
import { useChainInfo } from './hooks/useChainInfo';

export interface FreemiumChainGuardProps {
  children: ReactNode;
}

export const FreemiumChainGuard = ({ children }: FreemiumChainGuardProps) => {
  const { isInitialized, isSignedIn } = useAuthInstance();

  const isSignedOutManually = useAppSelector(selectIsSignedOutManually);

  const blockhainsLoading = useAppSelector(selectBlockchainsLoadingStatus);

  const { isPremiumOnly, isPublic } = useChainInfo();

  const currentPath = useCurrentPath();

  const signIn = useSignInWithRedirectURL({
    hasAPIKeysNotification: true,
    redirectURL: currentPath,
  });

  const shouldSignIn =
    !isSignedIn &&
    !isSignedOutManually &&
    !isPublic &&
    !blockhainsLoading &&
    !isPremiumOnly;

  useEffect(() => {
    if (shouldSignIn) {
      signIn();
    }
  }, [signIn, shouldSignIn]);

  const hasPlaceholder = !isInitialized || blockhainsLoading || shouldSignIn;
  const shouldRedirectToChainsPage =
    isSignedOutManually && !isPublic && !isPremiumOnly;

  return (
    <Placeholder
      hasPlaceholder={hasPlaceholder}
      placeholder={<OverlaySpinner />}
    >
      <Placeholder
        hasPlaceholder={shouldRedirectToChainsPage}
        placeholder={<RedirectToChainsList />}
      >
        {children}
      </Placeholder>
    </Placeholder>
  );
};
